import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  CanActivate,
  Router,
  CanMatch,
  Route,
  UrlSegment,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { AUTH_REDIRECT_URL } from '../auth.tokens';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanMatch {
  private router = inject(Router);
  private authService = inject(AuthService);
  private redirectUrl = inject(AUTH_REDIRECT_URL);

  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    const allowedRoles: string[] | undefined = route.data?.['roles'];
    const allowedPermissions: string[] | undefined = route.data?.['permissions'];

    return this.checkAccess(allowedRoles, allowedPermissions);
  }

  canMatch(
    route: Route,
    _segments: UrlSegment[]
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    const allowedRoles: string[] | undefined = route.data?.['roles'];
    const allowedPermissions: string[] | undefined = route.data?.['permissions'];

    return this.checkAccess(allowedRoles, allowedPermissions);
  }

  private checkAccess(
    allowedRoles?: string[],
    allowedPermissions?: string[]
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    const token = this.authService.getToken?.();
    if (!token) {
      return this.router.createUrlTree(['/', this.redirectUrl]);
    }

    if (!allowedRoles?.length && !allowedPermissions?.length) {
      return true;
    }

    return this.authService.currentUser$.pipe(
      take(1),
      switchMap((user) => (user ? of(user) : this.authService.getCurrentUser())),
      map((user) => {
        const permissions = user?.permissions ?? user?.data?.permissions;

        if (allowedPermissions?.length && Array.isArray(permissions)) {
          return allowedPermissions.some((permission) =>
            permissions.includes(permission)
          )
            ? true
            : this.router.createUrlTree(['/']);
        }

        if (!allowedRoles?.length) {
          return true;
        }

        const role = user?.role ?? user?.data?.role ?? null;
        return role && allowedRoles.includes(role)
          ? true
          : this.router.createUrlTree(['/']);
      }),
      catchError(() => of(this.router.createUrlTree(['/', this.redirectUrl])))
    );
  }
}
